import type { FunctionComponentWithRef } from "react";
import { graphql, useFragment } from "react-relay";

import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "scmp-app/components/content/content-item-render/types";
import type { livePrimaryContentItemContent$key } from "scmp-app/queries/__generated__/livePrimaryContentItemContent.graphql";

import {
  ActionBar,
  Container,
  CoverImage,
  Headline,
  ImageContainer,
  Section,
  StyledEntityLink,
  Summary,
} from "./styles";

export type Props = ContentItemRenderVariantProps & {
  reference: livePrimaryContentItemContent$key;
};

export const ContentItemLivePrimary: FunctionComponentWithRef<Props, HTMLDivElement> = ({
  className,
  onClick,
  ref: forwardReference,
  reference: reference_,
}) => {
  const content = useFragment(
    graphql`
      fragment livePrimaryContentItemContent on Content {
        entityId
        ...entityLink
        ...hooksContentItemProvidersContent
          @arguments(
            withSections: true
            withHeadline: true
            withSummary: true
            withActionBar: true
            withCoverImage: true
            withCoverImageSize540x360: true
            withVideoDurationOverlay: true
          )
        __typename
        ... on Video {
          duration
        }
      }
    `,
    reference_,
  );
  const providers = useContentItemProviders(content);

  return (
    <Container className={className} ref={forwardReference}>
      <Section>{providers.sections()}</Section>
      <Headline>
        <StyledEntityLink onClick={() => onClick?.(content.entityId)} reference={content}>
          {providers.headline({ preferSocialHeadline: true })}
        </StyledEntityLink>
      </Headline>
      <Summary>
        <StyledEntityLink onClick={() => onClick?.(content.entityId)} reference={content}>
          {providers.summary({
            isTextOverflowEllipsis: true,
          })}
        </StyledEntityLink>
      </Summary>
      <ActionBar>
        {providers.actionBar({
          commentVariant: "reverse",
          displayDateVariant: "publishedDate",
          isBookmarkVisible: false,
        })}
      </ActionBar>
      <ImageContainer $isShowCoverImage={content.__typename === "Video"}>
        <StyledEntityLink onClick={() => onClick?.(content.entityId)} reference={content}>
          <CoverImage>
            {providers.coverImage({
              responsiveVariants: {
                desktopUp: "size540x360",
                mobileUp: "size540x360",
                tabletUp: "size540x360",
              },
              withCaption: false,
            })}
          </CoverImage>
          {content.duration &&
            providers.video?.durationOverlay({
              variant: "homepage",
            })}
        </StyledEntityLink>
      </ImageContainer>
    </Container>
  );
};

ContentItemLivePrimary.displayName = "ContentItemLivePrimary";
